import { debounce } from 'lodash';
import Denque from 'denque';

export enum LogMode {
    STORE_AND_DISPLAY = 'STORE_AND_DISPLAY', // Console, Memory, Persistent Storage (localStorage)
    DISPLAY_ONLY = 'DISPLAY_ONLY',         // Console, Memory (NO Persistent Storage)
    IGNORE = 'IGNORE'                      // No Console, No/Minimal Memory, No Persistent Storage
}

const getEffectiveLogMode = (): LogMode => {
    const envMode = process.env.REACT_APP_LOG_MODE?.toUpperCase();

    switch (envMode) {
        case LogMode.STORE_AND_DISPLAY:
            return LogMode.STORE_AND_DISPLAY;
        case LogMode.DISPLAY_ONLY:
            return LogMode.DISPLAY_ONLY;
        case LogMode.IGNORE:
            return LogMode.IGNORE;
        default:
            if (process.env.NODE_ENV === 'development') {
                return LogMode.DISPLAY_ONLY;
            }
            return LogMode.IGNORE;
    }
};


const CURRENT_LOG_MODE: LogMode = getEffectiveLogMode();
const LOG_STORAGE_KEY = 'LOG_STORAGE';
const MAX_LOG_AGE_MS = 15 * 60 * 1000; // 15 minutes
const MAX_LOG_ENTRIES = 1000; // Maximum number of log entries to keep
const DEBOUNCE_SAVE_MS = 15000;
const MAX_WAIT_SAVE_MS = 30000; // Force a save at least every 30 seconds

console.log(`Logger Mode Initialized: ${CURRENT_LOG_MODE}`);

interface AppLog {
    timestamp: number;
    log: string;
}

let appLogs: Denque<AppLog> = new Denque<AppLog>();
let initialLogsLoaded = false;

const loadInitialLogs = () => {
    if (initialLogsLoaded) return;

    if (CURRENT_LOG_MODE === LogMode.STORE_AND_DISPLAY) {
        try {
            const savedLogs = localStorage.getItem(LOG_STORAGE_KEY);
            if (savedLogs) {
                const parsedLogs = JSON.parse(savedLogs) as AppLog[];
                appLogs = new Denque<AppLog>(parsedLogs); // Initialize Denque with parsed logs
                pruneLogs(); // Initial prune after loading
            }
        } catch (err) {
            console.error("Error loading initial logs:", err);
        }
    }
    initialLogsLoaded = true;
};

const persistLogsToLocalStorage = () => {
    try {
        if (appLogs.length > 0) {
            localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(appLogs.toArray()));
        } else {
            localStorage.removeItem(LOG_STORAGE_KEY);
        }
    } catch (err) {
        console.error("Error saving logs to localStorage:", err);
    }
};

const manageLogsDebounced = debounce(() => {
    if (CURRENT_LOG_MODE === LogMode.STORE_AND_DISPLAY || CURRENT_LOG_MODE === LogMode.DISPLAY_ONLY) {
        pruneLogs();
    }

    if (CURRENT_LOG_MODE === LogMode.STORE_AND_DISPLAY) {
        persistLogsToLocalStorage();
    }
}, DEBOUNCE_SAVE_MS, {
    maxWait: MAX_WAIT_SAVE_MS
});

const pruneLogs = () => {
    const now = Date.now();

    // 1. Prune by time
    while (appLogs.length > 0 && (now - appLogs.peekFront()!.timestamp > MAX_LOG_AGE_MS)) {
        appLogs.shift(); // O(1) operation
    }

    // 2. Prune by size
    while (appLogs.length > MAX_LOG_ENTRIES) {
        appLogs.shift(); // O(1) operation
    }
};

// Initialize logs when the module is first imported/loaded
loadInitialLogs();

// Ensure logs are saved on page close if debouncing is long.
window.addEventListener('beforeunload', () => {
    if (CURRENT_LOG_MODE === LogMode.STORE_AND_DISPLAY) {
        manageLogsDebounced.flush(); // Flushes pruning and persisting
    }
});

const formatArguments = (args: any[]): string => {
    return args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' ');
};

export const logger = (...args: any[]): void => {
    if (CURRENT_LOG_MODE === LogMode.IGNORE) {
        return; // Do nothing if ignoring
    }

    const messageContent = formatArguments(args);
    const message = `${new Date().toISOString()}: ${messageContent}`;

    // Display to console for relevant modes
    if (CURRENT_LOG_MODE === LogMode.STORE_AND_DISPLAY || CURRENT_LOG_MODE === LogMode.DISPLAY_ONLY) {
        console.log(message);
    }

    // Add to in-memory buffer for relevant modes (so getCurrentLogs works)
    if (CURRENT_LOG_MODE === LogMode.STORE_AND_DISPLAY || CURRENT_LOG_MODE === LogMode.DISPLAY_ONLY) {
        const now = Date.now();
        appLogs.push({ timestamp: now, log: message });
        manageLogsDebounced(); // Trigger the debounced management (pruning and conditional saving)
    }
};

export const getCurrentLogs = (): AppLog[] => {
    if (CURRENT_LOG_MODE === LogMode.IGNORE) {
        return [];
    }
    // For STORE_AND_DISPLAY and DISPLAY_ONLY, appLogs is populated and pruned by manageLogsDebounced.
    return appLogs.toArray();
};
